import React, { useState, useEffect } from 'react';
import { FaStar, FaInfoCircle } from "react-icons/fa";
import { supabase } from './data/supabaseClient';
import './DetailedRatings.css';
import { useTranslation } from 'react-i18next';
import { RefreshButton } from './Templates';

const DetailedRatings = ({ userId = null }) => {
  const { t } = useTranslation('common');
  const [ratingData, setRatingData] = useState({
    reviewRating: 0,
    hasReviews: false,
    reviewCount: 0,
    activityScore: 0,
    overallScore: 0,
    reviewBreakdown: {}, // Will store count of each star rating
    latestReview: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch rating data
  useEffect(() => {
    fetchRatingData();
  }, [userId]);

  const fetchRatingData = async () => {
    try {
      setIsLoading(true);
      
      // Get current user if userId not provided
      let targetUserId = userId;
      if (!targetUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        targetUserId = user.id;
      }
      
      // Fetch profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('activity_score, overall_score')
        .eq('id', targetUserId)
        .single();
        
      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return;
      }

      // Fetch reviews data
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .eq('reviewee_id', targetUserId)
        .order('created_at', { ascending: false });
        
      if (reviewsError) {
        console.error('Error fetching reviews:', reviewsError);
        return;
      }
      
      // Calculate average rating and build breakdown
      let averageRating = 0;
      let hasReviews = false;
      let breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      
      if (reviewsData && reviewsData.length > 0) {
        const totalRating = reviewsData.reduce((sum, review) => {
          // Update breakdown
          if (review.rating >= 1 && review.rating <= 5) {
            breakdown[review.rating]++;
          }
          return sum + review.rating;
        }, 0);
        
        averageRating = totalRating / reviewsData.length;
        hasReviews = true;
      }
      
      // Get the latest review with user info
      let latestReview = null;
      if (reviewsData && reviewsData.length > 0) {
        // Get the reviewer info for the latest review
        const { data: reviewerData } = await supabase
          .from('profiles')
          .select('username, avatar_url')
          .eq('id', reviewsData[0].reviewer_id)
          .single();
          
        latestReview = {
          ...reviewsData[0],
          reviewer: reviewerData || { username: 'Unknown User' }
        };
      }
      
      // Update state with all the data
      setRatingData({
        reviewRating: averageRating,
        hasReviews: hasReviews,
        reviewCount: reviewsData?.length || 0,
        activityScore: parseFloat(profileData.activity_score) || 0,
        overallScore: parseFloat(profileData.overall_score) || 0,
        reviewBreakdown: breakdown,
        latestReview: latestReview
      });
    } catch (err) {
      console.error('Error loading rating data:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Refresh scores
  const refreshScores = async () => {
    try {
      setIsRefreshing(true);
      
      // Get current user if userId not provided
      let targetUserId = userId;
      if (!targetUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        targetUserId = user.id;
      }
      
      // Trigger score recalculation
      const { error } = await supabase.rpc('calculate_overall_score', {
        p_user_id: targetUserId
      });
      
      if (error) {
        console.error('Error refreshing scores:', error);
      }
      
      // Wait a moment for calculation to take effect
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Refetch all rating data
      await fetchRatingData();
    } catch (error) {
      console.error('Error refreshing scores:', error);
      setIsRefreshing(false);
    }
  };

  // Render stars with a specific color
  const renderStars = (rating, color, name) => {
    return [1, 2, 3, 4, 5].map((value) => (
      <FaStar 
        key={`${name}-${value}`}
        className={`star-icon ${value <= Math.round(rating) ? 'active' : ''}`}
        style={{ color: value <= Math.round(rating) ? color : undefined }}
      />
    ));
  };
  
  // Calculate percentage for review breakdown
  const getPercentage = (count) => {
    if (ratingData.reviewCount === 0) return 0;
    return Math.round((count / ratingData.reviewCount) * 100);
  };

  if (isLoading) {
    return <div className="detailed-ratings-loading">Loading ratings...</div>;
  }

  return (
    <div className="detailed-ratings-container">
      <div className="ratings-header">
        <h3>
          {t('profile.ratings_and_reviews')}
          <span className="ratings-count">
            ({ratingData.reviewCount} {t('profile.reviews')})
          </span>
        </h3>
        <RefreshButton 
          onClick={refreshScores}
          disabled={isRefreshing}
          tooltip={t('profile.refresh_scores_tooltip', 'Refresh your scores')}
          size="medium"
        />
      </div>
      
      <div className="ratings-summary">
        <div className="overall-score">
          <h4>{t('profile.overall_score')}</h4>
          <div className="score-display">
            <span className="large-score">{ratingData.overallScore.toFixed(1)}</span>
            <div className="star-rating large">
              {renderStars(ratingData.overallScore, 'var(--accent-color)', 'overall-large')}
            </div>
          </div>
          <div className="score-description">
            <FaInfoCircle className="info-icon" />
            <p>{t('profile.overall_score_description')}</p>
          </div>
        </div>
        
        <div className="scores-breakdown">
          <div className="score-component">
            <div className="component-header">
              <h5>{t('profile.review_rating')}</h5>
              <span className="component-value">{ratingData.reviewRating.toFixed(1)}</span>
            </div>
            <div className="star-rating medium">
              {renderStars(ratingData.reviewRating, 'var(--accent-color)', 'review-medium')}
            </div>
            <p className="component-description">
              {ratingData.hasReviews 
                ? t('profile.based_on_reviews', { count: ratingData.reviewCount })
                : t('profile.no_reviews_yet')}
            </p>
          </div>
          
          <div className="score-component">
            <div className="component-header">
              <h5>{t('profile.activity_score')}</h5>
              <span className="component-value">{ratingData.activityScore.toFixed(1)}</span>
            </div>
            <div className="star-rating medium">
              {renderStars(ratingData.activityScore, 'var(--accent-color)', 'activity-medium')}
            </div>
            <p className="component-description">
              {t('profile.activity_score_description')}
            </p>
          </div>
        </div>
      </div>
      
      {ratingData.hasReviews && (
        <div className="review-breakdown">
          <h4>{t('profile.rating_distribution')}</h4>
          <div className="breakdown-bars">
            {[5, 4, 3, 2, 1].map(stars => (
              <div key={`breakdown-${stars}`} className="breakdown-bar-item">
                <div className="breakdown-label">
                  <span>{stars}</span>
                  <FaStar className="breakdown-star" />
                </div>
                <div className="breakdown-bar-container">
                  <div 
                    className="breakdown-bar-fill"
                    style={{ 
                      width: `${getPercentage(ratingData.reviewBreakdown[stars])}%`,
                      backgroundColor: stars >= 4 ? 'var(--accent-color)' : (stars >= 3 ? 'var(--accent-color2)' : 'var(--accent-color2)') 
                    }}
                  ></div>
                </div>
                <span className="breakdown-percentage">
                  {getPercentage(ratingData.reviewBreakdown[stars])}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {ratingData.latestReview && (
        <div className="latest-review">
          <h4>{t('profile.latest_review')}</h4>
          <div className="review-card">
            <div className="review-header">
              <span className="reviewer-name">{ratingData.latestReview.reviewer.username}</span>
              <div className="review-rating">
                {renderStars(ratingData.latestReview.rating, 'var(--accent-color)', 'latest-review')}
              </div>
            </div>
            <p className="review-comment">{ratingData.latestReview.comment}</p>
            <div className="review-date">
              {new Date(ratingData.latestReview.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailedRatings;